import { getDaysFromToday, toFormattedDate, DATE_FORMAT } from 'utils/date';
import isEmpty from 'lodash/isEmpty';
import { COUNTRY_BASED_WORDING } from 'resources/company/hooks/useCountryBasedWording';
import { COUNTRIES } from 'resources/company/company.constants';
import { DEAL_STATUSES, DEAL_STARTED_STATUSES, DEAL_NOT_STARTED_STATUSES, DEAL_TYPES, DEAL_STAGES } from './deal.constants';
import { getDealMilestoneDueDate, getDealAuditDueDate } from 'resources/milestone/dueDate.helpers';
import { ACTIVE_DEALS_SEQUENCE } from 'pages/client-profile/[id]/index.constants';


const BUYER_PRE_CONTRACT_DEAL_STAGES = [
  DEAL_STAGES.PRE_CONTRACT,
  DEAL_STAGES.PRE_CONTRACT_HOME_SEARCH,
  DEAL_STAGES.PRE_CONTRACT_MAKE_OFFER,
];

export const isBuyerDeal = (dealType) => dealType === DEAL_TYPES.BUYER;

export const isSellerDeal = (dealType) => dealType === DEAL_TYPES.SELLER;

export const isBuyerPreContractDeal = ({ dealType, dealStage }) => dealType === DEAL_TYPES.BUYER
  && BUYER_PRE_CONTRACT_DEAL_STAGES?.includes(dealStage);

export const isPreContractBuyerDeal = (deal) => deal
  && deal.type === DEAL_TYPES.BUYER
  && Object.values(DEAL_NOT_STARTED_STATUSES).includes(deal.status);

export const hasNotStartedTransaction = (deal) => deal
  && (Object.values(DEAL_NOT_STARTED_STATUSES).includes(deal.status));

export const hasActiveTransaction = (deals) => {
  const dealsList = deals.length ? deals : [deals];

  return dealsList.some((deal) => deal.status === DEAL_STATUSES.ACTIVE);
};

export const hasStartedTransaction = (deal) => deal && Object.values(DEAL_STARTED_STATUSES).includes(deal.status);

export const isFinished = (deal) => deal && (isCompleted(deal) || isVoided(deal));

export const isCompleted = (deal) => deal && deal.status === DEAL_STATUSES.CLOSED;

export const isVoided = (deal) => deal && deal.status === DEAL_STATUSES.VOIDED;

export const hasFinishedTransaction = (deal) => isFinished(deal)
  && deal?.transaction
  && deal.transaction.created_on;

export const getActiveDeals = (deals) => deals?.filter(
  (deal) => ACTIVE_DEALS_SEQUENCE.includes(deal.status) && deal.property,
);

export const getFinishedDeals = (deals) => deals?.filter((deal) => isFinished(deal));

export const getDealStage = (deal) => {
  if (isFinished(deal)) {
    return null;
  }
  if (hasNotStartedTransaction(deal)) {
    if (deal.status === DEAL_STATUSES.ACTIVE_LISTING) return DEAL_STAGES.PRE_CONTRACT_ACTIVE_LISTING;
    return DEAL_STAGES.PRE_CONTRACT;
  }
  return DEAL_STAGES.UNDER_CONTRACT;
};

export const getDealStageTextFormat = ({
  deal,
  countryBasedWording = COUNTRY_BASED_WORDING[COUNTRIES.UNITED_STATES],
}) => {
  if (isFinished(deal)) {
    return null;
  }

  if (hasNotStartedTransaction(deal)) {
    if (deal.status === DEAL_STATUSES.ACTIVE_LISTING)
      return 'Pre-Contract (Active Listing)';

    return deal.type === DEAL_TYPES.SELLER ? 'Pre-Listing' : 'Pre-Contract';
  }

  return countryBasedWording.UNDER_CONTRACT;
};

export const sortDealsByStatusSequence = (deals, statusesSequence) => {
  if (!deals || !statusesSequence) return [];

  return statusesSequence.flatMap((status) => deals.filter((deal) => deal.status === status));
};

/**
 * @param {Deal} deal
 * @returns {string | null} Local date only ISO string
 * @example
 * getTransactionStartDate({ transaction: { created_on: '2024-11-01' }, created_on: '2024-10-25T05:35:07.000Z' }) => '2024-11-01'
 * getTransactionStartDate({ created_on: '2024-10-25T05:35:07.000Z' }) => '2024-10-25'
 */
export const getTransactionStartDate = (deal) => {
  const localStartDate = deal?.transaction?.created_on;
  if (localStartDate)
    return localStartDate;

  const utcCreatedDateTime = deal?.created_on;
  if (!utcCreatedDateTime)
    return null;

  const localCreatedDate = toFormattedDate(utcCreatedDateTime, DATE_FORMAT.ISO);
  return localCreatedDate;
};

/**
 * @param {Deal} deal
 * @returns {string | null} Local date only ISO string
 * @example
 * getTransactionClosingOn({ transaction: { closing_on: '2024-10-30' } }) => '2024-10-30'
 */
export const getTransactionClosingOn = (deal) => {
  const localClosingDate = deal?.transaction?.closing_on;
  if (!localClosingDate)
    return null;

  return localClosingDate;
};

/**
 * @param {Deal} deal
 * @returns {string | null} UTC date & time ISO string
 * @example
 * getTransactionFinishedOn({ transaction: { finished_on: '2024-10-30T05:10:00.000Z' } }) => '2024-10-30T05:10:00.000Z'
 */
export const getTransactionFinishedOn = (deal) => {
  const utcFinishedDateTime = deal?.transaction?.finished_on;
  if (!utcFinishedDateTime)
    return null;

  return utcFinishedDateTime;
};

/**
 * @param {Deal} deal
 * @returns {string | null} Local date only ISO string
 * @example
 * getTransactionUnderContractDate({ transaction: { individual_under_contract_date: '2024-12-30', created_on: '2024-11-01' }, created_on: '2024-10-25T05:35:07.000Z' }) => '2024-12-30'
 * getTransactionUnderContractDate({ transaction: { created_on: '2024-11-01' }, created_on: '2024-10-25T05:35:07.000Z' }) => '2024-11-01'
 * getTransactionUnderContractDate({ created_on: '2024-10-25T05:35:07.000Z' }) => '2024-10-25'
 */
export const getTransactionUnderContractDate = (deal) => {
  const localUnderContractDate = deal?.transaction?.individual_under_contract_date;
  if (localUnderContractDate)
    return localUnderContractDate;

  const localStartDate = getTransactionStartDate(deal);
  return localStartDate;
};

export const isBuyerHomeSearchDeal = (deal) => (
  isBuyerPreContractDeal({ dealType: deal?.type, dealStage: getDealStage(deal) }) && isEmpty(deal?.property?.address)
);

export const isBuyerMakeOfferDeal = (deal) => isBuyerPreContractDeal({
  dealType: deal?.type, dealStage: getDealStage(deal),
}) && !isEmpty(deal?.property?.address);

export const getAddressLine1 = (deal) => deal?.property?.address?.line1;
export const getAddressLine2 = (deal) => deal?.property?.address?.line2;
export const getAddressCity = (deal) => deal?.property?.address?.city;
export const getAddressState = (deal) => deal?.property?.address?.state;
export const getAddressZipCode = (deal) => deal?.property?.address?.zip;
export const getAddressItems = (deal) => ({
  line1: getAddressLine1(deal),
  line2: getAddressLine2(deal),
  city: getAddressCity(deal),
  state: getAddressState(deal),
  zip: getAddressZipCode(deal),
});

export const getTransactionStreetAddress = (deal) => {
  const { line1, line2 } = getAddressItems(deal);

  const addressArray = [line1, line2].filter((item) => !!item);

  return addressArray.join(', ');
};

export const getTransactionAddress = (deal) => {
  const { line1, line2, city, state, zip } = getAddressItems(deal);

  const addressArray = [line1, line2, city].filter((item) => !!item);
  const zipState = [state, zip].filter((item) => !!item);

  return addressArray?.length && zipState?.length
    ? `${addressArray.join(', ')}, ${zipState.join(' ')}`
    : 'N/A';
};

export const sortByClosingDate = (deals) => deals
  // nearest date, nullish last
  ?.sort((first, second) => {
    const firstClosingOn = new Date(getTransactionClosingOn(first));
    const secondClosingOn = new Date(getTransactionClosingOn(second));

    if (firstClosingOn === secondClosingOn) {
      return new Date(first.created_on) - new Date(second.created_on);
    }

    if (firstClosingOn === null) {
      return 1;
    }

    if (secondClosingOn === null) {
      return -1;
    }

    return firstClosingOn - secondClosingOn;
  });

export const getTextByTransactionStatus = ({ status, deal, closingOn }) => {
  const isBuyerHomeSearch = isBuyerHomeSearchDeal(deal);
  const isBuyerMakeOffer = isBuyerMakeOfferDeal(deal);

  if (isBuyerMakeOffer || status === DEAL_STATUSES.OFFER_PENDING) {
    return 'Offer Pending';
  }

  if (isBuyerHomeSearch || status === DEAL_STATUSES.HOME_SEARCH) {
    return 'Home Search';
  }

  if (status === DEAL_STATUSES.ACTIVE) {
    const daysToClose = getDaysFromToday(closingOn);
    return `${daysToClose} ${daysToClose === '1' ? 'day' : 'days'} to close`;
  }

  if (status === DEAL_STATUSES.PRE_LISTING) {
    return 'Pre-Listing';
  }

  if (status === DEAL_STATUSES.HOME_LISTED_COMPLETED) {
    return 'Active';
  }

  if (status === DEAL_STATUSES.CLOSED) {
    return 'Completed';
  }

  if (status === DEAL_STATUSES.VOIDED) {
    return 'Voided';
  }

  if (status === DEAL_STATUSES.ACTIVE_LISTING) {
    return 'Active Listing';
  }

  return null;
};

export const isNotMySampleDeal = ({ deal, realtorId }) => deal.realtor_id !== realtorId && deal.is_sample;

export const filterSampleDeals = ({ deals, realtorId }) => {
  if (!deals?.length) {
    return [];
  }

  const dealsWithoutSample = deals.filter((deal) => !deal.is_sample);

  if (dealsWithoutSample.length > 0) {
    return dealsWithoutSample;
  }

  const mySampleDeals = deals.filter((deal) => deal.is_sample && deal.realtor_id === realtorId);

  return mySampleDeals;
};

export const getDealStageForEdit = (deal) => {
  if (isFinished(deal)) {
    return null;
  }

  if (hasStartedTransaction(deal)) {
    return DEAL_STAGES.UNDER_CONTRACT;
  }

  const isBuyerHomeSearch = isBuyerHomeSearchDeal(deal);
  const isBuyerMakeOffer = isBuyerMakeOfferDeal(deal);

  if (isBuyerHomeSearch) {
    return DEAL_STAGES.PRE_CONTRACT_HOME_SEARCH;
  }

  if (isBuyerMakeOffer) {
    return DEAL_STAGES.PRE_CONTRACT_MAKE_OFFER;
  }

  return DEAL_STAGES.PRE_CONTRACT;
};

export const getSortedDealMilestonesAndAudits = (deal, dealMilestonesAndAudits) => {
  if (!deal || !dealMilestonesAndAudits || dealMilestonesAndAudits.length === 0) return [];

  // sort milestones and audits by created_on date initially so all other sortings will be based on created_on date
  const sortedByCreatedDate = dealMilestonesAndAudits
    .sort((first, second) => new Date(first.created_on) - new Date(second.created_on));

  const itemsWithActualDueDates = sortedByCreatedDate.map((milestoneOrAudit) => {
    const luxonDueDate = milestoneOrAudit.isAudit
      ? getDealAuditDueDate({ audit: milestoneOrAudit, deal })
      : getDealMilestoneDueDate({ milestone: milestoneOrAudit, deal });

    return {
      ...milestoneOrAudit,
      dueDate: luxonDueDate,
    };
  });

  // -- get milestones WITHOUT Due Date --
  const noDueDate = itemsWithActualDueDates.filter((item) => !item.dueDate);

  // -- get other milestones & audits WITH Due Date sorted by actual due date -->
  const noDueDateIds = noDueDate.map((item) => item.id);
  const sortedByDueDate = itemsWithActualDueDates
    .filter((item) => !noDueDateIds.includes(item.id))
    .sort((first, second) => first.dueDate.toMillis() - second.dueDate.toMillis());

  return [
    ...sortedByDueDate,
    ...noDueDate,
  ];
};

